import React, { useCallback, useEffect, useState } from 'react';
import { Container, Grid, InputAdornment, Tab, Tabs, TextField, Theme, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { TabContext, TabPanel } from "@mui/lab";
import {getAllBuyerBookings} from "../../components/bookedSpaces/request/booked-spaces-requests";
import { withSnackbar } from "../../components/hocs/withSnackbar";
import { getErrorMessageForNonStandardAndStandardResponse } from "../../util/NetworkErrorUtil";
import ActiveBookings from "../../components/bookingsOverview/ActiveBookings";
import PendingBookings from '../../components/bookingsOverview/PendingBookings';
import BuyerBookingTab from "../../components/bookingsOverview/BuyerBookingTab";
import { Search } from "@mui/icons-material";
import type { Booking } from "../../types/Booking";
import type { Account } from "../../types/Account";
import { BuyerBookingTabs, BuyerBookingTabVerbiage, headerCells, setBuyerBookingTab } from "../../components/bookingsOverview/data";
import { formatCurrencyValue } from '../../util/PaymentUtils';
import { formatFrequency } from '@securspace/securspace-ui-kit';
import moment from 'moment';

// TODO: Delete this file once new Buyer Booking Page is approved and in production
const showEndDate = (data) => {
  const {endDateAdjusted, bookingSubscriptionStatus, frequency} = data;
  const CANCELLED = 'CANCELLED';
  const RECURRING = 'RECURRING';

  const isOneTime = frequency !== RECURRING;
  const isCancelled = bookingSubscriptionStatus === CANCELLED;

  return (
      isCancelled ||
      isOneTime  ||
      (endDateAdjusted === true)
  );
}

const generateEmptyAllocationArrays = () => {
  const result = [];
  Object.keys(BuyerBookingTabs).forEach(function(key) {
    result[key] = [];
  });
  return result;
}

function formatRateField(booking) {
  return `${formatCurrencyValue(booking.rate, true)}/${booking.durationType === 'MONTHLY' ? "month" : booking.durationType === 'DAILY' ? "day" : "week"}`;
}

function formatOverageRateField(booking) {
  if (booking?.chargeOverages && booking?.locationChargeOverages) {
    if (booking.brokered) {
      return formatCurrencyValue(booking.brokeredBuyerOverageRateCharged, true);
    } else {
      return formatCurrencyValue(booking.locationOverageRate, true);
    }
  }

  return '';
}

function transformBooking(booking) {
  const commonProperties = {
      initialCharge: formatCurrencyValue(booking.initialCharge, true),
      recurringCharge: formatCurrencyValue(booking.recurringCharge, true),
      rate: formatRateField(booking),
      overageRate: formatOverageRateField(booking),
      frequencyValue: formatFrequency(booking.frequency, booking.durationType),
      bookedDates: `${moment(booking.startDate).format("MM/DD/YYYY") || "N/A"} → ${showEndDate(booking) ? moment(booking.endDate).format("MM/DD/YYYY") : 'Until cancelled'}`
  };

  return {
      ...booking,
      ...commonProperties
  };
}

const useStyles: (theme: Theme) => { mainContainer: CSSStyleSheet, tabPanel: CSSStyleSheet } = makeStyles((theme) => ({
  mainContainer: {
    paddingTop: '2.5rem',
    paddingBottom: '2.86rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    [theme.breakpoints.up('sm')]: {
      paddingLeft: '8.04rem',
      paddingRight: '8.04rem',
    }
  },
  tabPanel: {
    paddingLeft: '0',
    paddingRight: '0',
  },
}));

const BookingsOverview = (props: { account: Account, snackbarShowMessage: () => void, readPendingBooking: () => {} }) => {
  const { account, snackbarShowMessage, readPendingBooking } = props;

  const urlParams = new URLSearchParams(window.location.search);
  const defaultSearch = urlParams.get("bookingNumber") || "";

  const getDefaultTab = () => {
    let buyerBookingTab = BuyerBookingTabs[urlParams?.get("status")?.toUpperCase()];
    return buyerBookingTab ? buyerBookingTab : BuyerBookingTabs.ACTIVE;
  }

  const [currentTab, setCurrentTab] = useState(getDefaultTab());
  const [bookings: Booking[], setBookings] = useState([]);
  const [allocatedBookings, setAllocatedBookings] = useState(generateEmptyAllocationArrays());
  const [reloadAfterUpdate, setReloadAfterUpdate] = useState(true);
  const [searchQuery, setSearchQuery] = useState(defaultSearch);
  const classes = useStyles();

  const allocateBookings = useCallback((bookings) => {
    const allocations = generateEmptyAllocationArrays();
    bookings.forEach((booking) => {
      allocations[booking.buyerBookingTab].push(booking);
    });
    setAllocatedBookings(allocations);
    return allocations;
  }, []);

  useEffect(() => {
    if (account?.id && reloadAfterUpdate) {
      getAllBuyerBookings('START_DATE','DESC').then((response) => {
        const bookings = response.body?.map((booking) => {
          return transformBooking(booking);
        })
        setBookings(bookings || []);

        bookings.forEach((booking) => setBuyerBookingTab(booking));

        allocateBookings(bookings);
      }).catch((error) => {
        snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(error));
      });
    }
    setReloadAfterUpdate(false);
  }, [account, reloadAfterUpdate, snackbarShowMessage, allocateBookings]);

  const handleTabChange = (_event, value) => {
    if (value !== undefined) {
      setCurrentTab(value);
    }
  };

  const handleSearchQueryChange = (event) => {
    const { value } = event.target;
    setSearchQuery(value);
  };

  const handleActionSuccess = () => {
    readPendingBooking();
    setReloadAfterUpdate(true);
  };

  useEffect(() => {
    if (searchQuery) {
      let filterTokens = searchQuery.split(" ").map(value => value.toLocaleLowerCase());

      const filteredBookings: Booking[] = bookings.filter((booking: Booking) => filterTokens.reduce((found: boolean, token: string) => found ||
          booking.orderNumber?.toLocaleLowerCase().includes(token) ||
          booking.assetType?.toLocaleLowerCase().includes(token) ||
          booking.locationName?.toLocaleLowerCase().includes(token) ||
          booking.locationCity?.toLocaleLowerCase().includes(token) ||
          booking.locationState?.toLocaleLowerCase().includes(token) ||
          booking.locationAddressLine1?.toLocaleLowerCase().includes(token) ||
          booking.startDate.includes(token) ||
          booking.endDate.includes(token), false));

      const allocations = allocateBookings(filteredBookings);
      if (filteredBookings.length && filteredBookings.length < bookings.length && !allocations[currentTab].length) {
        const firstTab = Object.keys(BuyerBookingTabs).find((tab) => !!allocations[tab].length);
        setCurrentTab(firstTab);
      }
    } else {
      allocateBookings(bookings);
    }
  }, [searchQuery, bookings, allocateBookings]);

  const formatTabLabel = (tab: string) => {
    const label = BuyerBookingTabVerbiage[tab].label;
    const count = allocatedBookings[tab].length;
    return count ? `${label} (${count})` : label;
  }

  return (
    <Container className={classes.mainContainer}>
      <Grid item>
        <Typography variant={'h5'} component={'h1'}>My Booking Overview</Typography>
      </Grid>
      <Grid item>
        <TabContext value={currentTab}>
          <Grid container justifyContent={'space-between'} alignItems={'center'}>
            <Grid item xs md={8}>
              <Tabs value={currentTab} onChange={handleTabChange} aria-label={'My Bookings Tabs'} textColor={'secondary'} variant={'fullWidth'} >
                {Object.values(BuyerBookingTabs).map((tab) => { return <Tab key={tab} value={tab} label={formatTabLabel(tab)} /> })}
              </Tabs>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                name={'searchQuery'}
                value={searchQuery}
                onChange={handleSearchQueryChange}
                label={'Search'}
                placeholder={'Enter your query here'}
                InputProps={{
                  endAdornment: <InputAdornment position={'end'}>
                    <Search />
                  </InputAdornment>
                }}
                variant={'standard'}
                fullWidth
              />
            </Grid>
          </Grid>

          <TabPanel value={BuyerBookingTabs.ACTIVE} className={classes.tabPanel}>
            <ActiveBookings bookings={allocatedBookings[BuyerBookingTabs.ACTIVE]} onActionSuccess={handleActionSuccess} account={account} headerCells={headerCells} />
          </TabPanel>
          <TabPanel value={BuyerBookingTabs.PENDING} className={classes.tabPanel}>
            <PendingBookings bookings={allocatedBookings[BuyerBookingTabs.PENDING]} account={account} onActionSuccess={handleActionSuccess} headerCells={headerCells}/>
          </TabPanel>
          <TabPanel value={BuyerBookingTabs.INACTIVE} className={classes.tabPanel}>
            <BuyerBookingTab tab={BuyerBookingTabs.INACTIVE} bookings={allocatedBookings[BuyerBookingTabs.INACTIVE]} onActionSuccess={handleActionSuccess} account={account} searchQuery={searchQuery} headerCells={headerCells} />
          </TabPanel>
          <TabPanel value={BuyerBookingTabs.PAYMENT_FAILED} className={classes.tabPanel}>
            <BuyerBookingTab tab={BuyerBookingTabs.PAYMENT_FAILED} bookings={allocatedBookings[BuyerBookingTabs.PAYMENT_FAILED]} onActionSuccess={handleActionSuccess} account={account} searchQuery={searchQuery} headerCells={headerCells} />
          </TabPanel>
        </TabContext>
      </Grid>
    </Container>
  );
};

export default withSnackbar(BookingsOverview);
